import React from 'react';
import { Checkbox, Col, Divider, Form, Input, Row, Typography } from 'antd';
import { PhoneInput, SubmitButton } from '@common/components';
import { TimeZoneSelect } from '@identity/components';
import { useMutation } from '@common/hooks';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  UpdateCurrentWorksUserDetailsMutation,
  UpdateCurrentWorksUserDetailsMutationVariablesType,
} from '@identity/graphql';
import Auth from 'works/legacy/lib/auth';
import { useCurrentMembership } from '@identity/hooks';
import { FontSize, FontWeight } from '@common/tokens';

type FormInputData =
  UpdateCurrentWorksUserDetailsMutationVariablesType['input'];

export function UserSettings() {
  useSetBreadcrumbs([
    {
      name: 'Account Settings',
      path: '/works/account-settings',
    },
    {
      name: 'User Settings',
      path: '/works/account-settings/user-settings',
    },
  ]);
  const [form] = Form.useForm<FormInputData>();
  const currentMembership = useCurrentMembership();

  const errorMessage = () =>
    createMessage('error', 'Something went wrong. Try again later.');

  const [save, { loading: mutationLoading }] = useMutation(
    UpdateCurrentWorksUserDetailsMutation,
    {
      refetchQueries: ['AccountSettingsCurrentWorksUser'],
    }
  );

  function onSubmit(formValues: FormInputData) {
    save({
      variables: {
        input: formValues,
      },
    })
      .then(({ data }) => {
        if (!data?.updateCurrentWorksUserDetails.currentWorksUser) {
          errorMessage();
          return;
        }

        createMessage('success', 'User details were updated successfully');

        Auth.update({ saveLastVisitedPage: formValues.saveLastVisitedPage });
      })
      .catch(errorMessage);
  }

  const { firstName, lastName, phone, timeZone, saveLastVisitedPage } =
    currentMembership.worksUser || {};

  return (
    <>
      <div className="user-settings-page">
        <Typography.Title
          level={3}
          style={{
            fontSize: FontSize.lg,
            margin: '24px 0',
            fontWeight: FontWeight.medium,
          }}
        >
          User Details
        </Typography.Title>
        <Divider />
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{
            firstName,
            lastName,
            phone,
            saveLastVisitedPage,
            timeZone,
          }}
        >
          <Row gutter={[24, 0]}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[
                  { required: true, message: 'Please enter a first name' },
                ]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[
                  { required: true, message: 'Please enter a last name' },
                ]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[
                  { required: true, message: 'Please enter a phone number' },
                  {
                    pattern: /^(\d{3}-){2}\d{4}$/,
                    message: 'Must be in xxx-xxx-xxxx format',
                  },
                ]}
              >
                <PhoneInput size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="timeZone"
                label="Time Zone"
                rules={[
                  { required: true, message: 'Please enter a time zone' },
                ]}
              >
                <TimeZoneSelect placeholder="Select Time Zone" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="saveLastVisitedPage" valuePropName="checked">
            <Checkbox defaultChecked={saveLastVisitedPage || undefined}>
              Default landing page is the last visited page before logging out
            </Checkbox>
          </Form.Item>

          <div style={{ textAlign: 'left', marginTop: 32 }}>
            <SubmitButton loading={mutationLoading}>Save</SubmitButton>
          </div>
        </Form>
      </div>
    </>
  );
}
